import { useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAppDispatch } from "hooks";
import { addFileThunk } from "./documentCart.slice";
import { CART_SIZE_LIMIT_LABEL } from "./document-cart.service";

export interface AddFilesToCartSummary {
  added: number;
  alreadyInCart: number;
  noAccess: number;
  failed: number;
  truncatedChildren: number;
  /** Ids that were NOT added and NOT already in the cart — keep these selected. */
  remainingIds: string[];
}

const EMPTY_SUMMARY: AddFilesToCartSummary = {
  added: 0,
  alreadyInCart: 0,
  noAccess: 0,
  failed: 0,
  truncatedChildren: 0,
  remainingIds: [],
};

interface UseAddFilesToCartOptions {
  /** Matches documents vs profile visibility when adding. */
  source?: "document" | "profile";
}

// A 403 with no body (see documentCart.slice's getCartErrorCode — a Sails
// `res.forbidden()` drops the payload) surfaces as the exact string
// "Forbidden" (from the response's statusText). A future JSON body on that
// same 403 could instead carry an explicit "FORBIDDEN" / "NO_ACCESS" code —
// treat both the same way, case-insensitively.
const NO_ACCESS_CODES = new Set(["FORBIDDEN", "NO_ACCESS"]);
const isNoAccessCode = (code: string) =>
  NO_ACCESS_CODES.has(code.toUpperCase());

/**
 * Bulk "add to cart" for a set of file ids — same sequential approach as
 * categoryFiles.tsx's handleAddSelectedToCart, packaged as a reusable hook so
 * other screens (e.g. the customer documents tab) can offer the same action.
 */
export default function useAddFilesToCart({
  source = "document",
}: UseAddFilesToCartOptions = {}) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { companyId } = useParams<{ companyId: string }>();
  const [isAdding, setIsAdding] = useState(false);

  const addFiles = useCallback(
    async (fileIds: string[]): Promise<AddFilesToCartSummary> => {
      if (!companyId || fileIds.length === 0) return EMPTY_SUMMARY;

      const summary: AddFilesToCartSummary = {
        added: 0,
        alreadyInCart: 0,
        noAccess: 0,
        failed: 0,
        truncatedChildren: 0,
        remainingIds: [],
      };
      // Set once a CART_FULL / CART_TOO_LARGE (or ZIP_TOO_LARGE, same bucket
      // per AddToCartButton) response is hit — the rest of the batch would
      // fail the same way, so we stop attempting and just carry the
      // remaining ids over as "not attempted". FILE_TOO_LARGE and
      // FILE_SIZE_UNKNOWN are per-file instead — they don't say anything
      // about the other ids, so the loop keeps going.
      let cartStopReason: "CART_FULL" | "CART_TOO_LARGE" | null = null;
      let fileTooLargeCount = 0;
      let fileSizeUnknownCount = 0;
      let otherFailedCount = 0;

      setIsAdding(true);
      try {
        for (const fileId of fileIds) {
          if (cartStopReason) {
            summary.remainingIds.push(fileId);
            continue;
          }
          try {
            // eslint-disable-next-line no-await-in-loop
            const result = await dispatch(
              addFileThunk({ companyId, fileId, source })
            ).unwrap();
            if (result.alreadyInCart) {
              summary.alreadyInCart += 1;
            } else {
              summary.added += 1;
              if (result.truncated) {
                summary.truncatedChildren += result.skippedChildren || 0;
              }
            }
          } catch (code) {
            const errCode = String(code);
            if (isNoAccessCode(errCode)) {
              summary.noAccess += 1;
              summary.remainingIds.push(fileId);
            } else if (errCode.includes("CART_FULL")) {
              summary.failed += 1;
              summary.remainingIds.push(fileId);
              cartStopReason = "CART_FULL";
            } else if (
              errCode.includes("CART_TOO_LARGE") ||
              errCode.includes("ZIP_TOO_LARGE")
            ) {
              summary.failed += 1;
              summary.remainingIds.push(fileId);
              cartStopReason = "CART_TOO_LARGE";
            } else if (errCode.includes("FILE_TOO_LARGE")) {
              summary.failed += 1;
              summary.remainingIds.push(fileId);
              fileTooLargeCount += 1;
            } else if (errCode.includes("FILE_SIZE_UNKNOWN")) {
              summary.failed += 1;
              summary.remainingIds.push(fileId);
              fileSizeUnknownCount += 1;
            } else {
              summary.failed += 1;
              summary.remainingIds.push(fileId);
              otherFailedCount += 1;
            }
          }
        }
      } finally {
        setIsAdding(false);
      }

      if (summary.truncatedChildren > 0) {
        toast.message(
          t("document_cart_added_children_partial", {
            count: summary.truncatedChildren,
          })
        );
      } else if (summary.added > 0) {
        toast.success(t("document_cart_added"));
      }

      if (summary.noAccess > 0) {
        toast.error(
          t("document_cart_add_no_access", { count: summary.noAccess })
        );
      }

      if (cartStopReason === "CART_FULL") {
        toast.error(t("document_cart_full"));
      } else if (cartStopReason === "CART_TOO_LARGE") {
        toast.error(
          t("document_cart_too_large", { limit: CART_SIZE_LIMIT_LABEL })
        );
      }

      if (fileTooLargeCount > 0) {
        toast.error(
          t("document_cart_file_too_large", { limit: CART_SIZE_LIMIT_LABEL })
        );
      }
      if (fileSizeUnknownCount > 0) {
        toast.error(t("document_cart_file_size_unknown"));
      }
      if (otherFailedCount > 0) {
        toast.error(t("document_cart_add_failed"));
      }

      if (
        summary.added === 0 &&
        summary.failed === 0 &&
        summary.noAccess === 0 &&
        summary.alreadyInCart > 0
      ) {
        toast.message(t("document_cart_already_in"));
      }

      return summary;
    },
    [companyId, source, dispatch, t]
  );

  return { addFiles, isAdding };
}
